import * as React from 'react';
import { useState, useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'next-i18next';
import { useAddressAdditionalFields } from '@restworld/utils-common';
import { FormLabelWrapper, TextInput } from '@restworld/ui-ds-base';
import { GoogleAutoCompleteInput } from '@restworld/ui-ds-base';

import { HelperTextRElement } from '../form-inputs';
import { WizardStepProps } from '../../worker-application-wizard.types';
import { useWorkerApplicationCxt } from '../../worker-application-wizard.context';

export const WizardSecondStep: React.FunctionComponent<WizardStepProps> = () => {
  const { t } = useTranslation();
  const { errorsTextMap } = useWorkerApplicationCxt();
  const { control, setValue, getValues } = useFormContext();
  const [address, setAddress] = useState<string>('');
  const onChange = (value: string) => setAddress(value);
  // initialFields: the saved form address already carries lat/lon, so re-entering this
  // step must not re-geocode it (billed call); only a new selection geocodes.
  const addressFields = useAddressAdditionalFields({
    addressString: address,
    initialFields: getValues('address') ?? undefined,
    surface: 'worker_signup_home_address',
  });
  useEffect(() => {
    if (addressFields && Object.keys(addressFields).length) {
      setValue('address', {
        address: addressFields.address,
        country: addressFields.country,
        address_lat: addressFields.address_lat,
        address_lon: addressFields.address_lon,
        administrative_area_level_1: addressFields.administrative_area_level_1,
        administrative_area_level_2: addressFields.administrative_area_level_2,
      });
      if (addressFields.postal_code) setValue('postal_code', addressFields.postal_code);
      if (addressFields.locality) setValue('locality', addressFields.locality);
    }
  }, [addressFields, setValue]);
  useEffect(() => {
    if (getValues('address')?.address) setAddress(getValues('address').address);
  }, [getValues]);

  return (
    <div>
      <div className="flex gap-4 flex-col md:flex-row md:justify-between">
        <div className="w-full mb-4">
          <FormLabelWrapper id={'address'} label={t('onboarding-page:your_home_address')}>
            <GoogleAutoCompleteInput
              formId="wizard-user-address"
              key="wizard-user-address"
              trackingSurface="worker_signup_home_address"
              placeholder="Via Roma, 12, Verona, VR, Italia"
              control={control}
              onSuggestionSelect={({ description }) => {
                onChange(description);
              }}
              formValue={address}
              testId="address-input"
              helperText={errorsTextMap['address']}
              placesFilterTypes={['address']}
              dropdownMainWrapperStyle={'max-w-menu'}
            />
          </FormLabelWrapper>
        </div>
      </div>
      <div className="flex gap-4 flex-col md:flex-row md:justify-between">
        <div className="md:w-[35%]">
          <FormLabelWrapper id={'postal_code'} label={'CAP'}>
            <TextInput
              id="postal_code"
              placeholder="00000"
              type="number"
              control={control}
              helperText={<HelperTextRElement helperText={errorsTextMap['postal_code']} />}
              color={errorsTextMap['postal_code'] ? 'failure' : undefined}
              rules={{ required: true }}
              disabled={!address}
              data-cy="postal-code-input"
            />
          </FormLabelWrapper>
        </div>
        <div className="w-full">
          <FormLabelWrapper id={'locality'} label={t('onboarding-page:city_where_you_live')}>
            <TextInput
              id="locality"
              placeholder="Torino"
              type="text"
              control={control}
              helperText={<HelperTextRElement helperText={errorsTextMap['locality']} />}
              color={errorsTextMap['locality'] ? 'failure' : undefined}
              rules={{ required: true }}
              disabled={!address}
              data-cy="locality-input"
            />
          </FormLabelWrapper>
        </div>
      </div>
    </div>
  );
};
export default WizardSecondStep;
